import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, ScrollText, Landmark, RefreshCw, ArrowUpRight, CheckCircle2, Users,
} from "lucide-react";

const PILLARS = [
  { icon: Landmark, title: "Client money held separately", body: "Cash sits in segregated, non-interest-bearing accounts with tier-1 banks — never lent out, never earning riba." },
  { icon: RefreshCw, title: "Re-screened every 30 days", body: "Every holding is re-tested against AAOIFI thresholds on live filings. A downgrade reaches you the same day." },
  { icon: ScrollText, title: "Purification, calculated for you", body: "We show the exact percentage of income to give away, per holding, and let you settle it in one tap." },
  { icon: Users, title: "A board, not an algorithm alone", body: "Screens are set and reviewed by our Shariah Supervisory Board, then applied consistently by the platform." },
];

export const GovernanceSection = ({ onCta }) => {
  const [cert, setCert] = useState(null);
  useEffect(() => { api.get("/halal/certification").then(({ data }) => setCert(data)).catch(() => {}); }, []);

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20" data-testid="governance-section">
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6D8] px-3 py-1 text-xs font-semibold text-[#3E2E65]">
            <ShieldCheck className="h-3.5 w-3.5" /> Shariah governance
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">
            Halal you can audit, not just trust
          </h2>
          <p className="mt-4 text-[#1A1330]/65 max-w-xl leading-relaxed">
            Every instrument on Tharwy is screened against the AAOIFI Shariah Standards — business
            activity first, then the financial ratios — and the working is published on the
            instrument itself. If a company drifts, you are told which screen it failed and by how much.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-black/5 bg-white p-5 transition-shadow hover:shadow-premium"
                data-testid={`governance-pillar-${p.title.split(" ")[0].toLowerCase()}`}>
                <p.icon className="h-5 w-5 text-[#3E2E65]" />
                <h3 className="mt-3 text-sm font-semibold">{p.title}</h3>
                <p className="mt-1.5 text-xs text-[#1A1330]/60 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          <Button className="mt-7 rounded-full px-6" onClick={onCta} data-testid="governance-cta">
            Meet the Shariah board <ArrowUpRight className="h-4 w-4 ms-1.5" />
          </Button>
        </div>

        <div className="rounded-3xl navy-strip text-white p-7 sm:p-9 relative overflow-hidden" data-testid="governance-board-card">
          <div className="absolute inset-0 hero-sheen opacity-50" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.18em] text-white/50">Supervisory board</div>
            <h3 className="mt-2 text-xl font-semibold">{cert?.board_name || "Tharwy Shariah Supervisory Board"}</h3>

            <div className="mt-6 space-y-3">
              {(cert?.scholars || []).map((s) => (
                <div key={s.name} className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-3.5"
                  data-testid={`governance-scholar-${s.role.toLowerCase()}`}>
                  <div className="h-9 w-9 rounded-xl bg-[color:var(--tharwy-lime)] text-[#0B2018] flex items-center justify-center text-xs font-bold">
                    {s.name.split(" ").slice(-1)[0].slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{s.name}</div>
                    <div className="text-[11px] text-white/55">{s.role} · {s.credentials}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
                <div className="text-white/50">Certificate</div>
                <div className="mt-1 numeric font-semibold">{cert?.certificate_id || "MWX-SHR-2026-001"}</div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
                <div className="text-white/50">Last audit</div>
                <div className="mt-1 numeric font-semibold">{cert?.last_audit || "2026-01-15"}</div>
              </div>
            </div>

            <ul className="mt-6 space-y-2">
              {(cert?.standards || ["AAOIFI Shariah Standards"]).map((st) => (
                <li key={st} className="flex items-start gap-2 text-sm text-white/80">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--tharwy-lime)] shrink-0" />{st}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[11px] text-white/45 leading-relaxed">
              {cert?.statement || "All halal products are screened against business activity, financial ratios and income purification."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
